/* eslint-disable @typescript-eslint/no-useless-constructor */
import { SupabaseClient } from '@supabase/supabase-js';
import { adminAuthClient } from '@grcs/data/init';
import { UserProfile, UserRole } from '@grcs/types/UserRole';
import { BaseRepository } from '../BaseRepository';
import {
  DatabaseArrayResult,
  StudentUserInput,
  TABLE_NAMES,
  UserLanguage,
} from '../types';

/**
 * User repository that handles all user-related database operations
 * Preserves all existing function signatures and business logic
 */
export class UserRepository extends BaseRepository {
  constructor(supabase: SupabaseClient<any, 'public', any>) {
    super(supabase);
  }

  /**
   * Preserves exact logic from getUserByEmail
   */
  async getUserByEmail(email: string): Promise<DatabaseArrayResult<any>> {
    if (!email) throw new Error('User email is not defined');
    return this.getDataById(TABLE_NAMES.USER, 'email', email);
  }

  /**
   * Preserves exact logic from getUserByUsername
   */
  async getUserByUsername(username: string): Promise<DatabaseArrayResult<any>> {
    if (!username) throw new Error('Username is not defined');
    return this.getDataById(TABLE_NAMES.USER, 'username', username);
  }

  /**
   * Preserves exact logic from getUserByUserToken
   */
  async getUserByUserToken(
    userToken: string,
  ): Promise<DatabaseArrayResult<any>> {
    if (!userToken) throw new Error('user_token is not defined');
    return this.getDataById(TABLE_NAMES.USER, 'user_token', userToken);
  }

  /**
   * Get user by authentication ID
   */
  async getUserByAuthId(
    authId: string | undefined,
  ): Promise<DatabaseArrayResult<any>> {
    if (!authId) throw new Error('Auth ID is not defined');
    const result = await this.getDataById(TABLE_NAMES.USER, 'auth_uid', authId);
    return { data: result.data?.[0] || null, error: result.error };
  }

  /**
   * Preserves exact logic from getUsersByRole
   */
  async getUsersByRole(role: string): Promise<DatabaseArrayResult<any>> {
    if (!role) throw new Error('User role is not defined');
    return this.getDataById(TABLE_NAMES.USER, 'role', role);
  }

  /**
   * Get preferred language by user type
   */
  async getUserLanguageByType(
    userType: string,
  ): Promise<DatabaseArrayResult<UserLanguage>> {
    if (!userType) throw new Error('User type is not defined');
    return this.getDataById(TABLE_NAMES.USER_LANGUAGE, 'user', userType, true);
  }

  /**
   * Preserves exact logic from addNewUserToDB
   */
  async addNewUser(user: UserProfile): Promise<DatabaseArrayResult<any>> {
    return this.insertData(TABLE_NAMES.USER, user);
  }

  /**
   * Preserves exact logic from addNewStudentUserToDB
   */
  async addNewStudentUser(
    input: StudentUserInput,
  ): Promise<DatabaseArrayResult<any>> {
    const { username, password, dob, email, first_name, last_name } = input;
    return this.insertData(TABLE_NAMES.USER, {
      username,
      password,
      dob,
      email,
      first_name,
      last_name,
      full_name: `${first_name} ${last_name}`,
      role: UserRole.STUDENT,
    });
  }

  /**
   * Legacy student-only credential write. Staff passwords must go through AccountService.
   * Return write failures to the caller so the UI cannot report false success.
   */
  async updatePasswordByUserToken(
    userToken: string,
    password: string,
  ): Promise<void> {
    const { data, error } = await this.supabase
      .from(TABLE_NAMES.USER)
      .update({ password })
      .eq('user_token', userToken)
      .eq('role', UserRole.STUDENT)
      .is('auth_uid', null)
      .eq('deleted', false)
      .select('user_token')
      .single();
    if (error) throw error;
    if (!data) throw new Error('The student password could not be updated.');
  }

  /**
   * Preserves exact logic from removeUserCompletely
   * TODO(Bocong): Figure out why auth admin level cannot remove teacher
   */
  async removeUserCompletely(
    userToken: string,
    authUid: string,
  ): Promise<void> {
    try {
      await this.updateDataById(TABLE_NAMES.USER, 'user_token', userToken, {
        deleted: true,
      });
      if (authUid !== '') {
        await adminAuthClient.deleteUser(authUid);
      }
    } catch (error) {
      console.error('Error deleting teacher:', error);
    }
  }

  /**
   * Preserves exact logic from getTeacherNameByTeacherToken
   */
  async getTeacherNameByTeacherToken(
    teacherToken: string,
  ): Promise<DatabaseArrayResult<any>> {
    if (!teacherToken) {
      return { data: null, error: 'Teacher token is not defined' };
    }

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.USER)
        .select('*')
        .eq('user_token', teacherToken)
        .eq('role', UserRole.TEACHER);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Generic update method for updating user data by ID
   */
  async updateUserById(
    userToken: string,
    userData: any,
  ): Promise<DatabaseArrayResult<any>> {
    return this.updateDataById(
      TABLE_NAMES.USER,
      'user_token',
      userToken,
      userData,
    );
  }

  /**
   * Generic method to access Supabase client for auth operations
   */
  get supabaseClient() {
    return this.supabase;
  }

  /**
   * Generic method to get all data
   */
  async getAllData<T>(
    tableName: string,
    excludeDeleted = true,
  ): Promise<DatabaseArrayResult<T>> {
    return super.getAllDataPublic<T>(tableName, excludeDeleted);
  }

  /**
   * Generic method to soft delete by ID
   */
  async softDeleteById(
    tableName: string,
    idName: string,
    idValue: string,
  ): Promise<void> {
    return super.softDeleteByIdPublic(tableName, idName, idValue);
  }

  /**
   * Generic method to insert data
   */
  async insertData(
    tableName: string,
    data: any,
  ): Promise<DatabaseArrayResult<any>> {
    return super.insertData(tableName, data);
  }

  /**
   * Generic method to update data by ID
   */
  async updateDataById(
    tableName: string,
    idName: string,
    idValue: string,
    updateData: any,
  ): Promise<DatabaseArrayResult<any>> {
    return super.updateDataById(tableName, idName, idValue, updateData);
  }

  /**
   * Generic method to get data by ID
   */
  async getDataById(
    tableName: string,
    idName: string,
    idValue: string,
    excludeDeleted = false,
  ): Promise<DatabaseArrayResult<any>> {
    return super.getDataById(tableName, idName, idValue, excludeDeleted);
  }

  /**
   * Authentication methods - abstracted supabase auth calls
   */
  async signInWithPassword(email: string, password: string): Promise<any> {
    return await this.supabase.auth.signInWithPassword({ email, password });
  }

  async signUp(email: string, password: string): Promise<any> {
    return await this.supabase.auth.signUp({ email, password });
  }

  async getSession(): Promise<any> {
    return await this.supabase.auth.getSession();
  }

  async signOut(): Promise<any> {
    return await this.supabase.auth.signOut();
  }

  /**
   * Functions invocation methods
   */
  async invokeFunction(functionName: string, body: any): Promise<any> {
    return await this.supabase.functions.invoke(functionName, { body });
  }
}
